import { useState } from "react";
import { api } from "../lib/api.js";

/* ============================================================
   Login screen — email + password, invite-gated.

   State machine (kept in `stage`):
     'email'         → single email input + Continue button.
                        Submitting POSTs /auth/check-email.
                        Response drives the next stage:
     'invited_new'   → email is on allowed_emails and no account
                        exists yet. Show password + confirm, then
                        POST /auth/register.
     'existing'      → account exists. Show password, POST /auth/login.
     'not_invited'   → email isn't on allowed_emails. Show a clear
                        error message + option to try a different email.

   All server errors surface inline. The "not invited" error is
   intentionally clear so the user knows to ask the owner to add
   them — no vague "sign-in failed" garbage.
   ============================================================ */

const C = {
  bg: "#0A1428",
  panel: "#132038",
  border: "#1F3355",
  text: "#D5DEE9",
  mute: "#8194AE",
  dim: "#54709A",
  amber: "#E8B339",
  red: "#FF5C5C",
  green: "#26D07C",
};

const inputBase = {
  width: "100%", boxSizing: "border-box",
  background: C.bg, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 8,
  padding: "11px 12px", fontSize: 14,
  fontFamily: "inherit", outline: "none",
};
const primaryBtn = {
  width: "100%", padding: "12px 16px",
  background: C.amber, color: C.bg,
  border: "none", borderRadius: 8,
  fontFamily: "inherit", fontSize: 14, fontWeight: 700,
  cursor: "pointer",
};
const secondaryBtn = {
  ...primaryBtn,
  background: "transparent", color: C.mute,
  border: `1px solid ${C.border}`,
  fontWeight: 500,
};

export default function Login() {
  const [stage, setStage] = useState("email");     // email | invited_new | existing | not_invited
  const [email, setEmail] = useState("");
  const [pw, setPw]       = useState("");
  const [pw2, setPw2]     = useState("");
  const [err, setErr]     = useState("");
  const [busy, setBusy]   = useState(false);

  const reset = () => {
    setStage("email"); setPw(""); setPw2(""); setErr("");
  };

  const onCheckEmail = async (e) => {
    e.preventDefault();
    setErr("");
    const cleaned = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      setErr("Enter a valid email address."); return;
    }
    setBusy(true);
    try {
      const res = await api.auth.checkEmail(cleaned);
      setStage(res.status);   // 'invited_new' | 'existing' | 'not_invited'
      setPw(""); setPw2("");
    } catch {
      setErr("Couldn't reach the server. Try again in a moment.");
    } finally { setBusy(false); }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 10)   { setErr("Password must be at least 10 characters."); return; }
    if (pw !== pw2)       { setErr("Passwords don't match.");                    return; }
    setBusy(true);
    try {
      await api.auth.register({ email: email.trim().toLowerCase(), password: pw });
      // Session cookie set — trigger the App.jsx whoami revalidation via full reload.
      window.location.reload();
    } catch (e) {
      // Distinguish the server's structured errors from a generic HTTP failure.
      const detail = e && e.body && e.body.error;
      if (detail === "not_invited")       setErr("This email isn't on the invite list. Ask the owner to add it.");
      else if (detail === "already_exists") setErr("An account already exists — sign in instead.");
      else if (detail === "password_too_short") setErr("Password must be at least 10 characters.");
      else                                setErr("Couldn't create the account. Try again.");
    } finally { setBusy(false); }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.auth.login({ email: email.trim().toLowerCase(), password: pw });
      window.location.reload();
    } catch (e) {
      const detail = e && e.body && e.body.error;
      if (detail === "bad_credentials") setErr("Wrong password. Try again.");
      else                              setErr("Sign-in failed. Try again.");
    } finally { setBusy(false); }
  };

  /* ---------- Render ---------- */
  return (
    <div style={{
      minHeight: "100vh", background: C.bg, color: C.text,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{
        width: "100%", maxWidth: 360,
        background: C.panel, border: `1px solid ${C.border}`,
        borderRadius: 14, padding: "34px 26px",
        display: "flex", flexDirection: "column", alignItems: "stretch", gap: 18,
      }}>
        <img src="/stocktweaks-logo.png" alt="StockTweaks"
             style={{ height: 36, width: "auto", alignSelf: "center" }} />

        {/* ------------------ Stage: email ------------------ */}
        {stage === "email" && (
          <form onSubmit={onCheckEmail} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 15, color: C.text, marginBottom: 6 }}>Sign in</div>
              <div style={{ fontSize: 11, color: C.mute, lineHeight: 1.5 }}>
                Your watchlist is private. Invite-only.
              </div>
            </div>
            <input
              type="email" autoComplete="email" autoCapitalize="none"
              autoFocus placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              style={inputBase} required
            />
            {err && <div style={{ fontSize: 11, color: C.red, textAlign: "center" }}>{err}</div>}
            <button type="submit" style={primaryBtn} disabled={busy || !email.trim()}>
              {busy ? "…" : "Continue"}
            </button>
          </form>
        )}

        {/* ------------------ Stage: invited_new (register) ------------------ */}
        {stage === "invited_new" && (
          <form onSubmit={onRegister} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, color: C.green, marginBottom: 6 }}>✓ You're on the invite list</div>
              <div style={{ fontSize: 11, color: C.mute, lineHeight: 1.5 }}>
                Create a password for <b style={{ color: C.text }}>{email.trim().toLowerCase()}</b>
              </div>
            </div>
            <input
              type="password" autoComplete="new-password" autoFocus
              placeholder="password (min 10 chars)"
              value={pw} onChange={(e) => setPw(e.target.value)}
              style={inputBase} minLength={10} required
            />
            <input
              type="password" autoComplete="new-password"
              placeholder="confirm password"
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              style={inputBase} minLength={10} required
            />
            {err && <div style={{ fontSize: 11, color: C.red, textAlign: "center" }}>{err}</div>}
            <button type="submit" style={primaryBtn} disabled={busy || pw.length < 10 || pw !== pw2}>
              {busy ? "…" : "Create account & sign in"}
            </button>
            <button type="button" onClick={reset} style={secondaryBtn}>← use a different email</button>
          </form>
        )}

        {/* ------------------ Stage: existing (login) ------------------ */}
        {stage === "existing" && (
          <form onSubmit={onLogin} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, color: C.text, marginBottom: 6 }}>Welcome back</div>
              <div style={{ fontSize: 11, color: C.mute, lineHeight: 1.5 }}>
                Signing in as <b style={{ color: C.text }}>{email.trim().toLowerCase()}</b>
              </div>
            </div>
            <input
              type="password" autoComplete="current-password" autoFocus
              placeholder="password"
              value={pw} onChange={(e) => setPw(e.target.value)}
              style={inputBase} required
            />
            {err && <div style={{ fontSize: 11, color: C.red, textAlign: "center" }}>{err}</div>}
            <button type="submit" style={primaryBtn} disabled={busy || !pw}>
              {busy ? "…" : "Sign in"}
            </button>
            <button type="button" onClick={reset} style={secondaryBtn}>← use a different email</button>
          </form>
        )}

        {/* ------------------ Stage: not_invited ------------------ */}
        {stage === "not_invited" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, color: C.red, marginBottom: 6 }}>Not invited</div>
              <div style={{ fontSize: 11, color: C.mute, lineHeight: 1.5 }}>
                <b style={{ color: C.text }}>{email.trim().toLowerCase()}</b> isn't on the invite list.
                Ask the owner to add it, then try again.
              </div>
            </div>
            <button type="button" onClick={reset} style={primaryBtn}>Try a different email</button>
          </div>
        )}
      </div>
    </div>
  );
}
